import clsx from "clsx";
import Image from "next/image";

import {
  ReservationAgreeButton,
  type ReservationAgreeButtonMessages,
} from "../../ui/buttons/reservation-agree-button";

export type HouseRulesMessages = {
  titleAlt: string;
  titleText: string;
  description: string;
  dishLine1: string;
  dishLine2: string;
  quiet22Line1: string;
  quiet21Line1: string;
  quietLine2: string;
  fireLine1: string;
  fireLine2: string;
  trashLine1: string;
  trashLine2: string;
  note: string;
  agreeButton: string;
  agreeButtonMessages?: ReservationAgreeButtonMessages;
};

const JA_MESSAGES: HouseRulesMessages = {
  titleAlt: "ハウスルール",
  titleText: "ハウスルール",
  description:
    "SANU 2nd Homeはホテルではなく、みんなでシェアする自然の中の別荘です。互いにルールを守りながら、大切に使っていただけると嬉しいです。",
  dishLine1: "お皿は洗って拭き、",
  dishLine2: "もとの場所へ",
  quiet22Line1: "22時以降は室内でお静かに",
  quiet21Line1: "21時以降は室内でお静かに",
  quietLine2: "お過ごしください",
  fireLine1: "火気は必ず焚き火台スペースで",
  fireLine2: "（焚き火台のない拠点では、火気の使用はご遠慮ください）",
  trashLine1: "ゴミは分別し、チェックアウト",
  trashLine2: "前にゴミステーションへ",
  note: "※詳しくは館内の案内をご確認ください。",
  agreeButton: "ハウスルールに同意します",
};

type ReservationHouseRulesProps = {
  onAgreementChange: (agreed: boolean) => void;
  agreed: boolean;
  isSiteOriginalIchinomiya: boolean;
  messages?: HouseRulesMessages;
};

const fontStyle = clsx([
  "ff-palt",
  "text-sm",
  "font-medium",
  "leading-relaxed",
  "tracking-[0.07rem]",
  "text-oldsblack",
]);

const RuleItem = ({
  icon,
  iconAlt,
  children,
}: {
  icon: string;
  iconAlt: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="bg-slightbeige-116 flex w-full flex-row items-center gap-[20px] rounded-xl p-[20px]">
      <div className="flex shrink-0 items-center justify-center">
        <Image src={icon} alt={iconAlt} width={60} height={60} />
      </div>
      <div className={fontStyle}>{children}</div>
    </div>
  );
};

const ReservationHouseRules = ({
  onAgreementChange,
  agreed,
  isSiteOriginalIchinomiya,
  messages = JA_MESSAGES,
}: ReservationHouseRulesProps) => {
  const handleCheckboxChange = (newAgreed: boolean) => {
    onAgreementChange?.(newAgreed);
  };

  return (
    <div className="bg-slightbeige mt-[30px] flex justify-center">
      <div className="flex w-full flex-col gap-[20px] p-[30px] md:w-[690px] md:px-0">
        <div className="border-sorange flex flex-col items-center justify-center gap-4 border-2 p-6 text-center">
          <Image
            src="/i/house_rules_title.svg"
            alt={messages.titleAlt}
            height={30}
            width={258}
          />
          <p className={clsx([fontStyle, "text-sorange", "text-lg"])}>
            {messages.titleText}
          </p>
        </div>

        <div className={fontStyle}>
          <p>{messages.description}</p>
        </div>

        <div className="grid grid-cols-1 gap-[20px] md:grid-cols-2">
          <RuleItem icon="/i/house_rules_dish.svg" iconAlt="dish">
            {messages.dishLine1}
            <br />
            {messages.dishLine2}
          </RuleItem>

          <RuleItem
            icon={`/i/house_rules_quiet_${
              isSiteOriginalIchinomiya ? "21" : "22"
            }00.svg`}
            iconAlt="quiet"
          >
            {isSiteOriginalIchinomiya
              ? messages.quiet21Line1
              : messages.quiet22Line1}
            <br />
            {messages.quietLine2}
          </RuleItem>

          <RuleItem icon="/i/house_rules_fire.svg" iconAlt="fire">
            {messages.fireLine1}
            <br />
            {messages.fireLine2}
          </RuleItem>

          <RuleItem icon="/i/house_rules_trash.svg" iconAlt="trash">
            {messages.trashLine1}
            <br />
            {messages.trashLine2}
          </RuleItem>
        </div>

        <div className={clsx([fontStyle, "text-xs"])}>
          <p>{messages.note}</p>
        </div>

        <div className="flex justify-center">
          <ReservationAgreeButton
            label={messages.agreeButton}
            required
            checked={agreed}
            onToggle={handleCheckboxChange}
            testId="house-rules-checkbox"
            messages={messages.agreeButtonMessages}
          />
        </div>
      </div>
    </div>
  );
};

export default ReservationHouseRules;
